import React, { useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Avatar,
  Divider,
  Skeleton,
  Alert,
  Stack,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Image as ImageIcon,
  TextSnippet as TextIcon,
  SmartButton as ButtonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as ApprovedIcon,
  PauseCircleFilled as PausedIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import BackBtn from "../../widgets/BackBtn";

// ExpandableText component for handling long text with show more/less
const ExpandableText = ({ text, maxLength = 150 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  if (text.length <= maxLength) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
        {text}
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
        {expanded ? text : `${text.substring(0, maxLength)}...`}
      </Typography>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ mt: 0.5, minWidth: "auto" }}
      >
        {expanded ? "Show less" : "Show more"}
      </Button>
    </>
  );
};

const TemplatesUsed = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useApiEp(
    `/root/used-templates-list/${id}`
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "success";
      case "PAUSED":
        return "warning";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return <ApprovedIcon fontSize="small" />;
      case "PAUSED":
        return <PausedIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "UTILITY":
        return "primary";
      case "MARKETING":
        return "secondary";
      default:
        return "default";
    }
  };

  const renderComponentIcon = (type) => {
    switch (type) {
      case "HEADER":
        return <ImageIcon fontSize="small" />;
      case "BODY":
        return <TextIcon fontSize="small" />;
      case "BUTTONS":
        return <ButtonIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Container sx={{ mt: 3 }}>
        <BackBtn />
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: 2 }}
              />
              <Box sx={{ pt: 1 }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ mt: 3 }}>
        <BackBtn />
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading templates: {error?.message || "Unknown error"}
        </Alert>
      </Container>
    );
  }

  const templates = data?.templates || [];

  return (
    <Container sx={{ mt: 3, mb: 4 }}>
      <BackBtn />

      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Templates for {data?.customerId || "Customer"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {templates.length} template{templates.length !== 1 ? "s" : ""} found
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: isMobile ? 1 : 0 }}>
          <Chip
            icon={<ApprovedIcon />}
            label={`${
              templates.filter((t) => t.status === "APPROVED").length
            } Approved`}
            size="small"
            variant="outlined"
            color="success"
          />
          <Chip
            icon={<PausedIcon />}
            label={`${
              templates.filter((t) => t.status === "PAUSED").length
            } Paused`}
            size="small"
            variant="outlined"
            color="warning"
          />
        </Stack>
      </Box>

      {templates.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No templates found for this customer.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  {/* Template Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      sx={{ wordBreak: "break-word", pr: 1 }}
                    >
                      {template.name}
                    </Typography>
                    <Stack spacing={1} alignItems="flex-end">
                      <Chip
                        icon={getStatusIcon(template.status)}
                        label={template.status}
                        size="small"
                        color={getStatusColor(template.status)}
                        variant="outlined"
                      />
                      <Chip
                        label={template.category}
                        size="small"
                        color={getCategoryColor(template.category)}
                      />
                    </Stack>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Template Components */}
                  <Box sx={{ mb: 2 }}>
                    <Stack spacing={2}>
                      {template.components.map((component, index) => (
                        <Box
                          key={index}
                          sx={{
                            p: 1.5,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: "primary.main",
                              }}
                            >
                              {renderComponentIcon(component.type)}
                            </Avatar>
                            <Typography
                              variant="subtitle2"
                              sx={{ textTransform: "capitalize" }}
                            >
                              {component.type.toLowerCase()}
                            </Typography>
                          </Box>

                          {component.type === "HEADER" && component.format && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Format: {component.format}
                              </Typography>
                              {component.example &&
                                component.example.header_handle && (
                                  <Tooltip title="Has example image">
                                    <ImageIcon
                                      color="action"
                                      fontSize="small"
                                    />
                                  </Tooltip>
                                )}
                            </Box>
                          )}

                          {component.type === "BODY" && component.text && (
                            <Box>
                              <ExpandableText text={component.text} />
                              {component.example &&
                                component.example.body_text && (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      p: 1,
                                      bgcolor: "grey.100",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      fontWeight="bold"
                                      display="block"
                                      gutterBottom
                                    >
                                      Example:
                                    </Typography>
                                    {component.example.body_text.map(
                                      (example, idx) => (
                                        <Typography
                                          key={idx}
                                          variant="caption"
                                          display="block"
                                          sx={{ fontStyle: "italic" }}
                                        >
                                          {example.join(", ")}
                                        </Typography>
                                      )
                                    )}
                                  </Box>
                                )}
                            </Box>
                          )}

                          {component.type === "BUTTONS" &&
                            component.buttons && (
                              <Stack
                                spacing={1}
                                direction="row"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {component.buttons.map((button, btnIndex) => (
                                  <Chip
                                    key={btnIndex}
                                    label={button.text}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ mb: 0.5 }}
                                  />
                                ))}
                              </Stack>
                            )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {/* Template Details */}
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 1,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <LanguageIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            Language:
                          </Typography>
                        </Box>
                        <Typography variant="caption" fontWeight="medium">
                          {template.language}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Parameter Format:
                        </Typography>
                        <Typography variant="caption" fontWeight="medium">
                          {template.parameter_format}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Template ID:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        >
                          {template.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TemplatesUsed;
